import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AggregateOptions,
  AggregationResponse,
  BulkInsertOptions,
  BulkInsertReferencesOptions,
  BulkInsertResponse,
  BulkRemoveOptions,
  BulkRemoveReferencesOptions,
  BulkRemoveResponse,
  BulkReplaceReferencesOptions,
  BulkSaveOptions,
  BulkSaveResponse,
  BulkUpdateOptions,
  BulkUpdateResponse,
  CountOptions,
  CountResponse,
  DistinctOptions,
  DistinctResponse,
  GetOptions,
  GetResponse,
  InsertOptions,
  InsertResponse,
  IsReferencedOptions,
  IsReferencedResponse,
  QueryOptions,
  QueryReferencedOptions,
  QueryReferencedResponse,
  QueryResponse,
  RemoveOptions,
  RemoveResponse,
  SaveOptions,
  SaveResponse,
  TruncateOptions,
  UpdateOptions,
  UpdateResponse,
  aggregate as universalAggregate,
  bulkInsert as universalBulkInsert,
  bulkInsertReferences as universalBulkInsertReferences,
  bulkRemove as universalBulkRemove,
  bulkRemoveReferences as universalBulkRemoveReferences,
  bulkReplaceReferences as universalBulkReplaceReferences,
  bulkSave as universalBulkSave,
  bulkUpdate as universalBulkUpdate,
  count as universalCount,
  distinct as universalDistinct,
  get as universalGet,
  insert as universalInsert,
  isReferenced as universalIsReferenced,
  query as universalQuery,
  queryReferenced as universalQueryReferenced,
  remove as universalRemove,
  save as universalSave,
  truncate as universalTruncate,
  update as universalUpdate,
} from './data-v1-data-service-data.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/data-backend-public-sdk-poc' };

/** @internal */
export function insert(httpClient: HttpClient): InsertSignature {
  return (options?: InsertOptions) =>
    universalInsert(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface InsertSignature {
  /**
   * Adds an item to a collection.
   *
   * When an item is inserted into a collection, the following properties and values are added to it:
   * * `_id`: A unique identifier for an item in a collection. You can optionally provide your own ID when inserting the item. If you specify an ID that already exists in the collection, the insertion will fail.
   * * `_createdDate`: The date the item was added to the collection.
   * * `_updatedDate`: The date the item was last modified. When the item is first inserted, `_createdDate` and `_updatedDate` have the same value.
   * @deprecated
   */
  (options?: InsertOptions): Promise<InsertResponse>;
}

/** @internal */
export function update(httpClient: HttpClient): UpdateSignature {
  return (options?: NonNullablePaths<UpdateOptions, `item`, 2>) =>
    universalUpdate(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateSignature {
  /**
   * Updates an item in a collection.
   *
   * An item ID must be submitted as part of the request, either as a path parameter or within the `item` body parameter. If an item is found in the specified collection with
   * the same ID, that item is updated. If the collection doesn't contain an item with that ID, the request fails.
   *
   * When an item is updated, its `_updatedDate` property is changed to the current date.
   *
   * **Note:**
   * After an item is updated, it only contains the properties included in the Update Data Item request. If the existing item has properties with values and those properties
   * aren't included in the updated item, their values are lost.
   * @deprecated
   */
  (
    options?: NonNullablePaths<UpdateOptions, `item`, 2>
  ): Promise<UpdateResponse>;
}

/** @internal */
export function save(httpClient: HttpClient): SaveSignature {
  return (options?: SaveOptions) =>
    universalSave(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SaveSignature {
  /**
   * Inserts or updates an item in a collection.
   *
   * The Save Data Item endpoint inserts or updates the specified item, depending on whether it already exists in the collection.
   *
   * * If you don't provide an ID, a new item is created.
   *
   * * If you provide an ID that doesn't exist in the collection, a new item is created with that ID.
   *
   * * If an item with the ID you provide already exists in the collection, that item is updated. When an item is updated, its `_updatedDate` property is changed to the current date.
   *
   * ***Note:*** When you provide an item with an ID that already exists in the collection, the item you provide completely replaces the existing item with that ID.
   * This means that all of the item's previous properties and values are lost.
   * @deprecated
   */
  (options?: SaveOptions): Promise<SaveResponse>;
}

/** @internal */
export function get(httpClient: HttpClient): GetSignature {
  return (itemId: string, options?: GetOptions) =>
    universalGet(
      itemId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetSignature {
  /**
   * Retrieves an item from a collection.
   * @param - ID of the item to retrieve.
   * @deprecated
   */
  (itemId: string, options?: GetOptions): Promise<GetResponse>;
}

/** @internal */
export function remove(httpClient: HttpClient): RemoveSignature {
  return (itemId: string, options?: RemoveOptions) =>
    universalRemove(
      itemId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveSignature {
  /**
   * Removes an item from a collection.
   *
   * If any items in other collections reference the removed item in a reference or multi-reference field, the field is cleared.
   *
   * **Note:**
   * Once an item has been removed from a collection, it can't be restored.
   * @param - ID of the item to remove.
   * @deprecated
   */
  (itemId: string, options?: RemoveOptions): Promise<RemoveResponse>;
}

/** @internal */
export function truncate(httpClient: HttpClient): TruncateSignature {
  return (options?: TruncateOptions) =>
    universalTruncate(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TruncateSignature {
  /**
   * Removes all items from a collection. Site owner (`ADMIN`) permissions are required in order to invoke this operation.
   *
   * If any items in other collections reference the removed items in reference or multi-reference fields, those fields are cleared.
   *
   * **Note:**
   * Once items have been removed from a collection, they can't be restored.
   * @deprecated
   */
  (options?: TruncateOptions): Promise<void>;
}

export function query(httpClient: HttpClient): QuerySignature {
  return (options?: QueryOptions) =>
    universalQuery(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QuerySignature {
  /**
   * Retrieves a list of items, on the basis of the filtering, sorting, and paging preferences you provide.
   *
   * For more details on using queries, see [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language).
   * @deprecated
   */
  (options?: QueryOptions): Promise<
    NonNullablePaths<
      QueryResponse,
      `items` | `totalCount` | `totalResults` | `partialIncludes`,
      2
    >
  >;
}

/** @internal */
export function aggregate(httpClient: HttpClient): AggregateSignature {
  return (options?: AggregateOptions) =>
    universalAggregate(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AggregateSignature {
  /** @deprecated */
  (options?: AggregateOptions): Promise<
    NonNullablePaths<AggregationResponse, `items`, 2>
  >;
}

/** @internal */
export function count(httpClient: HttpClient): CountSignature {
  return (options?: CountOptions) =>
    universalCount(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountSignature {
  /**
   * Returns the number of items in a collection that match a query.
   * @deprecated
   */
  (options?: CountOptions): Promise<
    NonNullablePaths<CountResponse, `totalCount` | `totalResults`, 2>
  >;
}

/** @internal */
export function distinct(httpClient: HttpClient): DistinctSignature {
  return (options?: NonNullablePaths<DistinctOptions, `propertyName`, 2>) =>
    universalDistinct(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DistinctSignature {
  /**
   * Retrieves a list of distinct values for a given property in all items that match a query, without duplicates.
   *
   * As with the [Query Data Items]() endpoint, this endpoint retrieves items based on the filtering, sorting, and paging preferences you provide.
   * However, the Query Distinct Values endpoint doesn't return all of the full items that match the query.
   * Rather, it returns all unique values of the property you specify in `propertyName` for items that match the query.
   * If more than one item has the same value for that property, that value appears only once.
   *
   * For more details on using queries, see [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language).
   * @deprecated
   */
  (options?: NonNullablePaths<DistinctOptions, `propertyName`, 2>): Promise<
    NonNullablePaths<DistinctResponse, `items`, 2>
  >;
}

/** @internal */
export function bulkInsert(httpClient: HttpClient): BulkInsertSignature {
  return (options?: NonNullablePaths<BulkInsertOptions, `items`, 2>) =>
    universalBulkInsert(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkInsertSignature {
  /**
   * Adds multiple items to a collection.
   *
   * When each item is inserted into a collection, the following properties and values are added to it:
   * * `_id`: A unique identifier for an item in a collection. You can optionally provide your own ID when inserting the item. If you specify an ID that already exists in the collection, the insertion will fail.
   * * `_createdDate`: The date the item was added to the collection.
   * * `_updatedDate`: The date the item was last modified. When the item is first inserted, `_createdDate` and `_updatedDate` have the same value.
   *
   * The endpoint returns a list of the IDs of items successfully inserted in `insertedItemIds`.
   * When any items can't be inserted, error details are provided for each failed item in `errors`.
   * @deprecated
   */
  (options?: NonNullablePaths<BulkInsertOptions, `items`, 2>): Promise<
    NonNullablePaths<
      BulkInsertResponse,
      | `insertedItemIds`
      | `errors`
      | `errors.${number}.message`
      | `errors.${number}.details.applicationError.code`
      | `errors.${number}.details.applicationError.description`
      | `errors.${number}.originalIndex`,
      6
    >
  >;
}

/** @internal */
export function bulkSave(httpClient: HttpClient): BulkSaveSignature {
  return (options?: NonNullablePaths<BulkSaveOptions, `items`, 2>) =>
    universalBulkSave(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkSaveSignature {
  /**
   * Inserts or updates multiple items in a collection.
   *
   * The Bulk Save Data Items endpoint inserts or updates each item, depending on whether it already exists in the collection. For each item:
   *
   * * If you don't provide an ID, a new item is created.
   *
   * * If you provide an ID that doesn't exist in the collection, a new item is created with that ID.
   *
   * * If an item with the ID you provide already exists in the collection, that item is updated. When an item is updated, its `_updatedDate` property is changed to the current date.
   *
   * The endpoint returns a list of the IDs of items successfully inserted in `insertedItemIds` and a list of the IDs of items successfully updated in `updatedItemIds`.
   * When any items can't be inserted or updated, error details are provided for each failed item in `errors`.
   *
   * ***Note:*** When you provide an item with an ID that already exists in the collection, the item you provide completely replaces the existing item with that ID.
   * This means that all of the item's previous properties and values are lost.
   * @deprecated
   */
  (options?: NonNullablePaths<BulkSaveOptions, `items`, 2>): Promise<
    NonNullablePaths<
      BulkSaveResponse,
      | `insertedItemIds`
      | `updatedItemIds`
      | `errors`
      | `errors.${number}.message`
      | `errors.${number}.details.applicationError.code`
      | `errors.${number}.details.applicationError.description`
      | `errors.${number}.originalIndex`,
      6
    >
  >;
}

/** @internal */
export function bulkUpdate(httpClient: HttpClient): BulkUpdateSignature {
  return (options?: NonNullablePaths<BulkUpdateOptions, `items`, 2>) =>
    universalBulkUpdate(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateSignature {
  /**
   * Updates multiple items in a collection.
   *
   * Each item in the request must include an ID. If an item is found in the specified collection with
   * the same ID, that item is updated. If the collection doesn't contain an item with that ID, the update fails.
   *
   * When an item is updated, its `_updatedDate` property is changed to the current date.
   *
   * The endpoint returns a list of the IDs of items successfully updated in `updatedItemIds`.
   * When any items can't be inserted, error details are provided for each failed item in `errors`.
   *
   * **Note:**
   * After each item is updated, it only contains the properties included in the Update Data Item request. If the existing item has properties with values and those properties
   * aren't included in the updated item, their values are lost.
   * @deprecated
   */
  (options?: NonNullablePaths<BulkUpdateOptions, `items`, 2>): Promise<
    NonNullablePaths<
      BulkUpdateResponse,
      | `updatedItemIds`
      | `errors`
      | `errors.${number}.message`
      | `errors.${number}.details.applicationError.code`
      | `errors.${number}.details.applicationError.description`
      | `errors.${number}.originalIndex`,
      6
    >
  >;
}

/** @internal */
export function bulkRemove(httpClient: HttpClient): BulkRemoveSignature {
  return (options?: BulkRemoveOptions) =>
    universalBulkRemove(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkRemoveSignature {
  /**
   * Removes multiple items from a collection.
   *
   * If any items in other collections reference the removed items in reference or multi-reference fields, the fields are cleared.
   *
   * The endpoint returns a list of the IDs of items successfully removed in `removedItemIds`.
   * When any items can't be removed, error details are provided for each failed item in `errors`.
   *
   * **Note:** Once an item has been removed from a collection, it can't be restored.
   * @deprecated
   */
  (options?: BulkRemoveOptions): Promise<
    NonNullablePaths<
      BulkRemoveResponse,
      | `removedItemIds`
      | `errors`
      | `errors.${number}.message`
      | `errors.${number}.details.applicationError.code`
      | `errors.${number}.details.applicationError.description`
      | `errors.${number}.originalIndex`,
      6
    >
  >;
}

/** @internal */
export function queryReferenced(
  httpClient: HttpClient
): QueryReferencedSignature {
  return (
    options?: NonNullablePaths<
      QueryReferencedOptions,
      `propertyName` | `referringItemId`,
      2
    >
  ) =>
    universalQueryReferenced(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryReferencedSignature {
  /**
   * Gets the full items referenced in the specified property of an item.
   *
   * For example, suppose you have a **Movies** collection with an **Actors** field that contains references to items in a **People** collection.
   * Querying the **Movies** collection using the Query Referenced Data Items endpoint returns the relevant **People** items referenced in the **Actors** field of the specified **Movie** item.
   * This gives you information from the **People** collection about each of the actors in the specified movie.
   * @deprecated
   */
  (
    options?: NonNullablePaths<
      QueryReferencedOptions,
      `propertyName` | `referringItemId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      QueryReferencedResponse,
      | `items`
      | `totalCount`
      | `totalResults`
      | `entities`
      | `entities.${number}.referenceId`,
      4
    >
  >;
}

/** @internal */
export function bulkInsertReferences(
  httpClient: HttpClient
): BulkInsertReferencesSignature {
  return (
    options?: NonNullablePaths<
      BulkInsertReferencesOptions,
      | `references`
      | `references.${number}.propertyName`
      | `references.${number}.referencedItemId`
      | `references.${number}.referringItemId`,
      4
    >
  ) =>
    universalBulkInsertReferences(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkInsertReferencesSignature {
  /**
   * Inserts one or more references in the specified properties of items in a collection.
   *
   * The Bulk Insert References endpoint adds one or more references to a collection.
   * Each new reference in the `references` field specifies a referring item's ID, the property in which to insert the reference, and the ID of the referenced item.
   * @deprecated
   */
  (
    options?: NonNullablePaths<
      BulkInsertReferencesOptions,
      | `references`
      | `references.${number}.propertyName`
      | `references.${number}.referencedItemId`
      | `references.${number}.referringItemId`,
      4
    >
  ): Promise<void>;
}

/** @internal */
export function bulkReplaceReferences(
  httpClient: HttpClient
): BulkReplaceReferencesSignature {
  return (
    options?: NonNullablePaths<
      BulkReplaceReferencesOptions,
      `propertyName` | `referringItemId`,
      2
    >
  ) =>
    universalBulkReplaceReferences(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkReplaceReferencesSignature {
  /**
   * Replaces references in a specified property of a specified data item.
   *
   * The Bulk Replace References endpoint replaces the existing reference or references contained in the property specified in `propertyName` within the data item specified in `referringItemId`.
   * The endpoint removes existing references and in their place it adds references to the items specified in `newReferencedItemIds`.
   * @deprecated
   */
  (
    options?: NonNullablePaths<
      BulkReplaceReferencesOptions,
      `propertyName` | `referringItemId`,
      2
    >
  ): Promise<void>;
}

/** @internal */
export function bulkRemoveReferences(
  httpClient: HttpClient
): BulkRemoveReferencesSignature {
  return (
    options?: NonNullablePaths<
      BulkRemoveReferencesOptions,
      `references` | `references.${number}.propertyName`,
      4
    >
  ) =>
    universalBulkRemoveReferences(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkRemoveReferencesSignature {
  /**
   * Removes one or more references.
   * @deprecated
   */
  (
    options?: NonNullablePaths<
      BulkRemoveReferencesOptions,
      `references` | `references.${number}.propertyName`,
      4
    >
  ): Promise<void>;
}

/** @internal */
export function isReferenced(httpClient: HttpClient): IsReferencedSignature {
  return (
    options?: NonNullablePaths<
      IsReferencedOptions,
      `propertyName` | `referencedItemId` | `referringItemId`,
      2
    >
  ) =>
    universalIsReferenced(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface IsReferencedSignature {
  /**
   * Checks if a specific property of a referring item contains a reference to another specific item.
   * @deprecated
   */
  (
    options?: NonNullablePaths<
      IsReferencedOptions,
      `propertyName` | `referencedItemId` | `referringItemId`,
      2
    >
  ): Promise<NonNullablePaths<IsReferencedResponse, `isReferenced`, 2>>;
}

export {
  AggregateOptions,
  AggregationRequest,
  AggregationResponse,
  ApplicationError,
  BulkError,
  BulkInsertOptions,
  BulkInsertReferencesOptions,
  BulkInsertReferencesRequest,
  BulkInsertReferencesResponse,
  BulkInsertRequest,
  BulkInsertResponse,
  BulkPatchRequest,
  BulkPatchResponse,
  BulkRemoveOptions,
  BulkRemoveReferencesOptions,
  BulkRemoveReferencesRequest,
  BulkRemoveReferencesResponse,
  BulkRemoveRequest,
  BulkRemoveRequestEntry,
  BulkRemoveResponse,
  BulkReplaceReferencesOptions,
  BulkReplaceReferencesRequest,
  BulkReplaceReferencesResponse,
  BulkSaveOptions,
  BulkSaveRequest,
  BulkSaveResponse,
  BulkUpdateOptions,
  BulkUpdateRequest,
  BulkUpdateResponse,
  CountOptions,
  CountRequest,
  CountResponse,
  CursorPaging,
  Cursors,
  Details,
  DistinctOptions,
  DistinctRequest,
  DistinctResponse,
  Entry,
  GetOptions,
  GetRequest,
  GetResponse,
  InsertOptions,
  InsertRequest,
  InsertResponse,
  IsReferencedOptions,
  IsReferencedRequest,
  IsReferencedResponse,
  Item,
  Options,
  Order,
  Paging,
  PagingMetadataV2,
  PatchRequest,
  PatchResponse,
  PatchWhereRequest,
  PatchWhereResponse,
  Query,
  QueryOptions,
  QueryReferencedOptions,
  QueryReferencedRequest,
  QueryReferencedResponse,
  QueryRequest,
  QueryResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  ReadOptions,
  Reference,
  ReferencedEntity,
  ReferencedEntityEntityOneOf,
  RemoveOptions,
  RemoveRequest,
  RemoveResponse,
  RestoreFromTrashBinRequest,
  RestoreFromTrashBinResponse,
  SaveOptions,
  SaveRequest,
  SaveResponse,
  Segment,
  SortOrder,
  Sorting,
  TruncateOptions,
  TruncateRequest,
  TruncateResponse,
  UpdateOptions,
  UpdateRequest,
  UpdateResponse,
} from './data-v1-data-service-data.universal.js';
